import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Wrench, Camera, BookOpen, Activity } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';

export default function Dashboard() {
    const navigate = useNavigate();
    const { config } = useBrand();
    const [prompt, setPrompt] = useState('');

    const handleSearch = () => {
        if (!prompt.trim()) return;
        navigate('/chat', { state: { initialPrompt: prompt } });
    };

    const quickActions = [
        { label: 'New Diagnosis', icon: <Wrench className="text-primary" />, action: () => navigate('/chat') },
        { label: 'AR Guide', icon: <Camera className="text-secondary" />, action: () => navigate('/ar') },
        { label: 'Part Lookup', icon: <Search className="text-accent" />, action: () => navigate('/parts') },
        { label: 'Diagrams', icon: <BookOpen className="text-primary" />, action: () => navigate('/diagrams') },
    ];

    // Fallback text if config isn't loaded yet or missing
    const brandName = config?.name || "INTELLIGENT REPAIR";
    const brandTagline = config?.tagline || "Professional Diagnostic For Any Situation - AR Augmented Reality Ready.";

    return (
        <div className="flex-1 overflow-y-auto">
            <div className="max-w-4xl mx-auto p-6 space-y-12 py-16">
                {/* Hero Section */}
                <div className="text-center space-y-4">
                    <h1 className="text-5xl font-black bg-gradient-to-r from-pink-400 to-primary bg-clip-text text-transparent tracking-tighter uppercase">
                        {brandName} <span className="text-white">ASK ABOUT ANYTHING</span>
                    </h1>
                    <p className="text-gray-400 text-lg max-w-xl mx-auto">
                        {brandTagline}
                    </p>
                </div>

                {/* Main Search */}
                <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-primary to-secondary rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                    <div className="relative flex bg-dark border border-slate/30 rounded-2xl overflow-hidden p-2 gap-2 shadow-2xl">
                        <input
                            type="text"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            placeholder="Describe your problem... (e.g. 'engine makes rhythmic clicking noise')"
                            className="flex-1 bg-transparent px-6 py-4 text-white text-lg focus:outline-none placeholder-gray-600"
                            autoFocus
                        />
                        <button
                            onClick={handleSearch}
                            disabled={!prompt.trim()}
                            className="bg-gradient-to-r from-primary to-secondary px-8 py-4 rounded-xl text-dark font-black tracking-widest uppercase hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                        >
                            Initialize
                        </button>
                    </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { label: 'Verified Fixes', value: '12,500+', icon: <Activity size={16} /> },
                        { label: 'Active Schematics', value: '84,000+', icon: <BookOpen size={16} /> },
                        { label: 'Global Registry', value: '150k+', icon: <Search size={16} /> },
                    ].map((stat, i) => (
                        <div key={i} className="bg-dark/40 border border-slate/20 p-6 rounded-2xl flex flex-col items-center text-center space-y-2">
                            <span className="text-primary/60">{stat.icon}</span>
                            <p className="text-2xl font-black text-white">{stat.value}</p>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">{stat.label}</p>
                        </div>
                    ))}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {quickActions.map((action, i) => (
                        <button
                            key={i}
                            onClick={action.action}
                            className="bg-dark/60 border border-slate/30 p-6 rounded-2xl hover:bg-primary/10 hover:border-primary/50 transition-all flex flex-col items-center gap-3 group"
                        >
                            <div className="p-3 bg-slate/10 rounded-xl group-hover:scale-110 transition-transform">
                                {action.icon}
                            </div>
                            <span className="text-white font-bold text-sm tracking-tight">{action.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
